import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@tinct/web-components';
import { Organization } from '@tinct/core-api-models';
import { pluralize } from '@tinct/core-utils';
import { useDeleteOrganizationForm } from '../hooks/use-delete-organization-form';
import { FormProvider } from 'react-hook-form';
import { IconCircleCheck } from '@tabler/icons-react';
import { Trash } from 'lucide-react';

type DeleteOrganizationModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organization: Organization;
};

export const DeleteOrganizationModal = ({
  open,
  onOpenChange,
  organization,
}: DeleteOrganizationModalProps) => {
  const { form, handleSubmit, isLoading, isConfirmationValid } = useDeleteOrganizationForm({
    organization,
    onSuccess: () => onOpenChange(false),
  });

  const campaignsCount = organization.campaigns?.length ?? 0;
  const membersCount = organization.statistics?.activeMembersCount ?? 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Delete Workspace</DialogTitle>
        </DialogHeader>

        {/* Destructive warning */}
        <div className="flex gap-3 items-start rounded-lg border border-rose-200 bg-rose-50 px-4 py-3">
          <div className="pt-0.5">
            <IconCircleCheck className="size-4 text-destructive" />
          </div>
          <div className="flex flex-col gap-1 flex-1 text-destructive">
            <p className="text-sm font-medium">
              This action will permanently delete
            </p>
            <ul className="text-sm list-disc ml-5 space-y-1">
              <li>All campaigns ({campaignsCount})</li>
              <li>All audiences</li>
              <li>All company data</li>
              <li>
                Remove all team members access ({membersCount}{' '}
                {pluralize(membersCount, 'user')})
              </li>
            </ul>
          </div>
        </div>

        {/* Subscription notice */}
        <div className="flex gap-3 items-start rounded-lg border px-4 py-3">
          <div className="flex flex-col gap-1 flex-1">
            <p className="text-sm font-medium">
              Subscription for <span className="font-medium">{organization.name}</span> will be canceled.
            </p>
            <div className="text-sm text-muted-foreground">
              <p>Subscription will be canceled at renewal date.</p>
              <p>You'll have 14 days to cancel the deletion via email.</p>
            </div>
          </div>
        </div>

        <FormProvider {...form}>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">
                Type "DELETE" to confirm:
              </label>
              <Input
                {...form.register('confirmation')}
                placeholder=""
                autoComplete="off"
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="destructive"
                isLoading={isLoading}
                disabled={!isConfirmationValid}
              >
                Schedule deletion
                <Trash className="size-4 ml-2" />
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};
